import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from './common/SafeIcon';
import LeadConverter from './components/LeadConverter';
import JourneyBuilder from './components/JourneyBuilder';
import CompareAndPitch from './components/CompareAndPitch';
import AgentProfile from './components/AgentProfile';
import ProfileBanner from './components/ProfileBanner';
import PaywallModal from './components/PaywallModal';
import Auth from './components/Auth';
import { ProfileProvider, useProfile } from './context/ProfileContext';
import './App.css';

const AppContent = () => {
  const [activeTab, setActiveTab] = useState('lead');
  const { user, isLoaded, setShowProfilePanel, usageCount, isSubscribed, signOut } = useProfile();

  if (!isLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <SafeIcon name="Loader" className="w-10 h-10 text-blue-600 animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <Auth />;
  }

  return (
    <div className="min-h-screen bg-slate-100 font-sans text-slate-900">
      <ProfileBanner />
      <PaywallModal />

      {/* Top Navigation */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-20 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-2">
              <div className="bg-blue-600 p-2 rounded-lg shadow-sm shadow-blue-200">
                <SafeIcon name="Navigation" className="text-white w-5 h-5" />
              </div>
              <span className="font-bold text-xl tracking-tight text-slate-800">Cruise CoPilot</span>
            </div>

            <nav className="flex items-center space-x-1 sm:space-x-4">
              <div className="flex items-center gap-4 mr-2 border-r border-slate-200 pr-2 md:pr-4">
                {/* Usage Indicator */}
                {!isSubscribed ? (
                  <div className="hidden sm:flex flex-col items-end">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Usage</span>
                    <span className="text-xs font-medium text-slate-600">{usageCount} of 10 free generations</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-1.5 bg-green-50 text-green-700 px-2.5 py-1 rounded-full border border-green-100">
                    <SafeIcon name="CheckCircle" className="w-3 h-3" />
                    <span className="text-xs font-bold uppercase tracking-wider">Pro</span>
                  </div>
                )}

                <div className="flex space-x-1 sm:space-x-2">
                  <button
                    onClick={() => setActiveTab('lead')}
                    className={`px-3 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${activeTab === 'lead' ? 'bg-blue-50 text-blue-700' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`}
                    title="Lead Converter"
                  >
                    <SafeIcon name="MessageSquare" />
                    <span className="hidden lg:inline">Lead Converter</span>
                  </button>
                  <button
                    onClick={() => setActiveTab('compare')}
                    className={`px-3 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${activeTab === 'compare' ? 'bg-blue-50 text-blue-700' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`}
                    title="Compare & Pitch"
                  >
                    <SafeIcon name="Repeat" />
                    <span className="hidden lg:inline">Compare & Pitch</span>
                  </button>
                  <button
                    onClick={() => setActiveTab('journey')}
                    className={`px-3 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${activeTab === 'journey' ? 'bg-blue-50 text-blue-700' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`}
                    title="Journey Builder"
                  >
                    <SafeIcon name="Map" />
                    <span className="hidden lg:inline">Journey Builder</span>
                  </button>
                </div>
              </div>

              <div className="flex items-center gap-1">
                <button
                  onClick={() => setShowProfilePanel(true)}
                  className="p-2 text-slate-500 hover:text-blue-600 hover:bg-slate-50 rounded-lg transition-all"
                  title="Agent Profile"
                >
                  <SafeIcon name="Settings" className="w-5 h-5" />
                </button>
                <button
                  onClick={signOut}
                  className="p-2 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                  title="Log Out"
                >
                  <SafeIcon name="LogOut" className="w-5 h-5" />
                </button>
              </div>
            </nav>
          </div>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-900 mb-2">
            {activeTab === 'lead' && 'Convert Leads to Bookings'}
            {activeTab === 'compare' && 'Compare Options & Pitch'}
            {activeTab === 'journey' && 'Design the Perfect Client Journey'}
          </h1>
          <p className="text-slate-500">
            {activeTab === 'lead' && 'Generate personalized, high-converting first contact emails instantly.'}
            {activeTab === 'compare' && 'Create narrative-driven comparisons that guide clients to the right booking.'}
            {activeTab === 'journey' && 'Automatically build a timed touchpoint strategy leading up to departure.'}
          </p>
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
          >
            {activeTab === 'lead' && <LeadConverter />}
            {activeTab === 'compare' && <CompareAndPitch />}
            {activeTab === 'journey' && <JourneyBuilder />}
          </motion.div>
        </AnimatePresence>
      </main>

      <AgentProfile />
    </div>
  );
};

function App() {
  return (
    <ProfileProvider>
      <AppContent />
    </ProfileProvider>
  );
}

export default App;